/**
 * API Service for the approval / review workflow (FECOA-1610..1614).
 *
 * Drives the shared review lifecycle (draft -> in_review -> approved/denied ->
 * published) exposed by the assessment and study-draft viewsets. Each call maps
 * to a custom @action endpoint on the backend transition service.
 */

import api from './api';

const AB_BASE = '/api/assessment-builder';
const SB_BASE = '/api/study-builder';

// ============================================================================
// ASSESSMENTS  (lookup by uuid)
// ============================================================================

export const submitAssessmentForReview = async (uuid) => {
  const response = await api.post(`${AB_BASE}/assessments/${uuid}/submit-for-review/`);
  return response.data;
};

export const withdrawAssessment = async (uuid) => {
  const response = await api.post(`${AB_BASE}/assessments/${uuid}/withdraw/`);
  return response.data;
};

export const approveAssessment = async (uuid, signedByName) => {
  const response = await api.post(`${AB_BASE}/assessments/${uuid}/approve/`,
    signedByName ? { signed_by_name: signedByName } : {});
  return response.data;
};

export const denyAssessment = async (uuid, reason) => {
  const response = await api.post(`${AB_BASE}/assessments/${uuid}/deny/`, { reason });
  return response.data;
};

export const unpublishAssessment = async (uuid) => {
  const response = await api.post(`${AB_BASE}/assessments/${uuid}/unpublish/`);
  return response.data;
};

export const getAssessmentReviewQueue = async () => {
  const response = await api.get(`${AB_BASE}/assessments/review-queue/`);
  return response.data;
};

// ============================================================================
// STUDY DRAFTS  (lookup by id)
// ============================================================================

export const submitStudyDraftForApproval = async (id) => {
  const response = await api.post(`${SB_BASE}/study-drafts/${id}/submit-for-approval/`);
  return response.data;
};

export const withdrawStudyDraft = async (id) => {
  const response = await api.post(`${SB_BASE}/study-drafts/${id}/withdraw/`);
  return response.data;
};

export const approveStudyDraft = async (id, signedByName) => {
  const response = await api.post(`${SB_BASE}/study-drafts/${id}/approve/`,
    signedByName ? { signed_by_name: signedByName } : {});
  return response.data;
};

export const denyStudyDraft = async (id, reason) => {
  const response = await api.post(`${SB_BASE}/study-drafts/${id}/deny/`, { reason });
  return response.data;
};

export const getStudyDraftReviewQueue = async () => {
  const response = await api.get(`${SB_BASE}/study-drafts/review-queue/`);
  return response.data;
};

// Publish an approved study draft: materializes the live Study + schedules and
// flips review_status approved -> published (FECOA-1599).
export const publishStudyDraft = async (id) => {
  const response = await api.post(`${SB_BASE}/study-drafts/${id}/publish/`);
  return response.data;
};

// ============================================================================
// Attestation history (FECOA-1611) — read-only ReviewSubmission records
// ============================================================================

// entity_type values the history endpoint understands (model names).
const HISTORY_ENTITY_TYPE = { assessment: 'assessment', study: 'studydraft' };

export const getReviewHistory = async (entityType, objectId) => {
  const response = await api.get('/api/review-submissions/', {
    params: {
      entity_type: HISTORY_ENTITY_TYPE[entityType] || entityType,
      object_id: objectId,
    },
  });
  return response.data;
};

// ============================================================================
// Approval enforcement toggle (FECOA-1646) — sponsor-level switch that gates
// whether the review workflow is required before publishing. Internal-admin only.
// ============================================================================

export const getSponsorEnforcement = async (sponsorId) => {
  const response = await api.get(`/api/sponsors/${sponsorId}/`);
  // tri-state: true (required) / false (off) / null (inherit env default)
  return response.data?.enforce_approval ?? null;
};

export const setSponsorEnforcement = async (sponsorId, value) => {
  const response = await api.patch(`/api/sponsors/${sponsorId}/enforce-approval/`, {
    enforce_approval: value,
  });
  return response.data;
};

// Study-level override: study setting wins over the sponsor setting, which wins
// over the environment default. tri-state true/false/null (null = inherit).
export const getStudyEnforcement = async (studyId) => {
  const response = await api.get(`/api/studies/${studyId}/`);
  return response.data?.enforce_approval_override ?? null;
};

export const setStudyEnforcement = async (studyId, value) => {
  const response = await api.patch(`/api/studies/${studyId}/enforce-approval/`, {
    enforce_approval_override: value,
  });
  return response.data;
};

// ============================================================================
// Permission names for client-side gating of approver UI (FECOA-1613)
// ============================================================================

export const APPROVE_ASSESSMENT_PERMISSION = 'assessment_builder.approve_assessment';
export const APPROVE_STUDY_PERMISSION = 'study_builder.approve_study';

// ============================================================================
// Shared review-state metadata for badges / labels
// ============================================================================

export const REVIEW_STATE = {
  DRAFT: 'draft',
  IN_REVIEW: 'in_review',
  APPROVED: 'approved',
  PUBLISHED: 'published',
  DENIED: 'denied',
  ARCHIVED: 'archived',
};
